// TraceFull v7 Phase 5 — Agent Intelligence Page
// Panels: Network Overview | Memory | Messaging | Scoring Queue | Router Stats | Schema Registry
import { useState, useEffect, useCallback } from 'react';
import { agentIntelApi, type AgentMemory, type AgentMessage, type RouterStats, type ScoringEvent, type ScoringQueueStats, type AgentSchema } from '../api/client';

import { T } from '../components/ui';

// ── Local alias (maps legacy C.* to canonical T.* tokens) ─────────────────────
const C = {
 bg: T.bg,
 surface: T.panel,
 border: T.border,
 border2: T.surface,
 text: T.text,
 muted: T.sub,
 dim: T.dim,
 copper: T.copper,
 gold: T.amber,
 sage: T.sage,
 blue: T.sapphire,
 crimson: T.crimson,
 amber: T.amber,
};

const AGENT_META: Record<string, { icon: string; color: string; label: string }> = {
 alert: { icon: '🔴', color: T.crimson, label: 'Alert' },
 provenance: { icon: '🌿', color: T.sage, label: 'Provenance' },
 procurement: { icon: '💼', color: T.amber, label: 'Procurement' },
 supply: { icon: '🔗', color: T.sapphire, label: 'Supply Chain' },
 legal: { icon: '⚖️', color: T.lavender, label: 'Legal' },
 finance: { icon: '💰', color: T.copper, label: 'Finance' },
 system: { icon: '⚙️', color: T.sub, label: 'System' },
 broadcast: { icon: '📡', color: T.sapphire, label: 'Broadcast' },
};

const TIER_COLOR: Record<string, string> = {
 haiku: T.sage,
 sonnet: T.sapphire,
 opus: T.copper,
};

const S = {
 page: { background: C.bg, minHeight: '100vh', padding: '28px 32px', fontFamily: "'DM Mono', 'Fira Code', monospace" } as React.CSSProperties,
 h1: { fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: '-0.3px', marginBottom: 4 } as React.CSSProperties,
 sub: { fontSize: 12, color: C.muted, marginBottom: 28, letterSpacing: '0.3px' } as React.CSSProperties,
 tabs: { display: 'flex', gap: 2, marginBottom: 24, borderBottom: `1px solid ${C.border}` } as React.CSSProperties,
 tab: (a: boolean): React.CSSProperties => ({ padding: '9px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: a ? C.copper : C.muted, borderBottom: `2px solid ${a ? C.copper : 'transparent'}`, marginBottom: -1, transition: 'all 0.15s', fontFamily: 'inherit' }),
 card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px', marginBottom: 14 } as React.CSSProperties,
 grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 } as React.CSSProperties,
 grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 } as React.CSSProperties,
 grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 } as React.CSSProperties,
 label: { fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 5, fontFamily: 'inherit' } as React.CSSProperties,
 num: (color = C.copper): React.CSSProperties => ({ fontSize: 28, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }),
 numSm: (color = C.text): React.CSSProperties => ({ fontSize: 18, fontWeight: 700, color }),
 table: { width: '100%', borderCollapse: 'collapse' } as React.CSSProperties,
 th: { padding: '8px 12px', textAlign: 'left', fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: `1px solid ${C.border}`, fontFamily: 'inherit' } as React.CSSProperties,
 td: { padding: '11px 12px', fontSize: 12, color: C.text, borderBottom: `1px solid ${C.border2}`, verticalAlign: 'middle' } as React.CSSProperties,
 pill: (color: string): React.CSSProperties => ({ display: 'inline-block', padding: '2px 9px', borderRadius: 20, background: color + '22', color, fontSize: 10, fontWeight: 700, letterSpacing: '0.3px', fontFamily: 'inherit' }),
 btn: (v: 'primary' | 'ghost' = 'ghost'): React.CSSProperties => ({ padding: '8px 16px', border: `1px solid ${v === 'primary' ? C.copper : C.border}`, background: v === 'primary' ? C.copper + '22' : 'transparent', color: v === 'primary' ? C.copper : C.muted, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.4px', fontFamily: 'inherit' }),
 empty: { textAlign: 'center', padding: '36px 0', color: C.dim, fontSize: 12 } as React.CSSProperties,
 mono: { fontFamily: "'DM Mono', monospace", fontSize: 11, color: C.muted } as React.CSSProperties,
};

function Bar({ value, max, color }: { value: number; max: number; color: string }) {
 return (
 <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: 'hidden', marginTop: 4 }}>
 <div style={{ height: '100%', width: `${Math.min(100, (value / (max || 1)) * 100)}%`, background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
 </div>
 );
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
 return (
 <div style={{ ...S.card, marginBottom: 0 }}>
 <div style={S.label}>{label}</div>
 <div style={S.num(color || C.copper)}>{value}</div>
 {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{sub}</div>}
 </div>
 );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Agent Network Overview (live status + Phase 5 capabilities)
// ─────────────────────────────────────────────────────────────────────────────
function NetworkTab() {
 const AGENTS = ['alert', 'provenance', 'procurement', 'supply', 'legal', 'finance'];
 const CAPABILITIES = [
 { id: '5.1', label: 'Persistent Memory', desc: 'pgvector semantic recall of past decisions' },
 { id: '5.2', label: 'A2A Messaging', desc: 'Redis pub/sub cross-agent communication' },
 { id: '5.3', label: 'Proactive Scoring', desc: 'Event-driven automatic re-scoring queue' },
 { id: '5.4', label: 'Model Router', desc: 'Haiku/Sonnet/Opus cost-optimised routing' },
 { id: '5.5', label: 'Structured Output', desc: 'tool_use schema enforcement — zero parse errors' },
 ];
 return (
 <div>
 <div style={S.grid4}>
 {AGENTS.map(a => {
 const m = AGENT_META[a];
 return (
 <div key={a} style={{ ...S.card, marginBottom: 0, borderLeft: `3px solid ${m.color}` }}>
 <div style={{ fontSize: 20, marginBottom: 6 }}>{m.icon}</div>
 <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>{m.label} Agent</div>
 <div style={S.pill(m.color)}>active</div>
 </div>
 );
 })}
 </div>

 <div style={S.card}>
 <div style={{ ...S.label, marginBottom: 14 }}>Phase 5 Capabilities — All Active</div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
 {CAPABILITIES.map(c => (
 <div key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: C.bg, borderRadius: 8, border: `1px solid ${C.border2}` }}>
 <div style={{ fontSize: 13, fontWeight: 800, color: C.copper, minWidth: 28, paddingTop: 1 }}>{c.id}</div>
 <div>
 <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 2 }}>{c.label}</div>
 <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{c.desc}</div>
 </div>
 <div style={{ marginLeft: 'auto', color: C.sage, fontSize: 14 }}>✓</div>
 </div>
 ))}
 </div>
 </div>

 <div style={{ ...S.card, background: '#0a1008', borderColor: '#1a3018' }}>
 <div style={{ fontSize: 11, color: C.sage, fontWeight: 700, marginBottom: 6 }}>🔐 Patent-defensible Architecture</div>
 <div style={{ fontSize: 12, color: '#8aaa80', lineHeight: 1.6 }}>
 The combination of multi-agent budget governance (Procurement Agent) + cross-agent messaging (5.2) + domain-specific vector memory (5.1) has no known prior art in production supply chain systems. Phase 5 strengthens patent claim 4.
 </div>
 </div>
 </div>
 );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Memory (5.1)
// ─────────────────────────────────────────────────────────────────────────────
function MemoryTab() {
 const [stats, setStats] = useState<any>(null);
 const [selected, setSelected] = useState('alert');
 const [memories, setMemories] = useState<AgentMemory[]>([]);
 const [total, setTotal] = useState(0);
 const [loading, setLoading] = useState(false);

 useEffect(() => { agentIntelApi.getMemoryStats().then(setStats).catch(() => {}); }, []);
 useEffect(() => {
 setLoading(true);
 agentIntelApi.getAgentMemories(selected, { limit: 30 })
 .then(d => { setMemories(d.memories); setTotal(d.total); })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, [selected]);

 const MEMORY_COLORS: Record<string, string> = {
 observation: C.blue, decision: C.gold, pattern: C.copper,
 anomaly: C.crimson, supplier_note: C.sage, product_note: '#a898d8',
 };

 return (
 <div>
 {stats && (
 <div style={S.grid3}>
 <StatCard label="Total Memories" value={stats.total} sub="Across all agents" />
 <div style={{ ...S.card, marginBottom: 0 }}>
 <div style={S.label}>By Agent</div>
 {(stats.by_agent || []).map((a: any) => (
 <div key={a.agent_type} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${C.border2}`, fontSize: 12 }}>
 <span style={{ color: C.text }}>{AGENT_META[a.agent_type]?.label || a.agent_type}</span>
 <span style={{ color: C.copper, fontWeight: 600 }}>{a.count}</span>
 </div>
 ))}
 </div>
 <div style={{ ...S.card, marginBottom: 0 }}>
 <div style={S.label}>By Type</div>
 {(stats.by_type || []).map((t: any) => (
 <div key={t.memory_type} style={{ marginBottom: 8 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
 <span style={S.pill(MEMORY_COLORS[t.memory_type] || C.muted)}>{t.memory_type}</span>
 <span style={{ color: C.muted }}>{t.count}</span>
 </div>
 <Bar value={parseInt(t.count)} max={stats.total} color={MEMORY_COLORS[t.memory_type] || C.dim} />
 </div>
 ))}
 </div>
 </div>
 )}

 <div style={S.card}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
 <div style={S.label}>Agent Memory Store</div>
 <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
 {Object.entries(AGENT_META).slice(0, 6).map(([k, v]) => (
 <button key={k} onClick={() => setSelected(k)} style={{ ...S.btn(selected === k ? 'primary' : 'ghost'), padding: '5px 10px' }}>
 {v.icon} {v.label}
 </button>
 ))}
 </div>
 </div>

 {loading ? (
 <div style={S.empty}>Loading memories…</div>
 ) : memories.length === 0 ? (
 <div style={S.empty}>No memories yet for {AGENT_META[selected]?.label} Agent. Run agents with FEATURE_REAL_AGENTS=true to accumulate memory.</div>
 ) : (
 <table style={S.table}>
 <thead><tr>
 <th style={S.th}>Type</th><th style={S.th}>Summary</th>
 <th style={S.th}>Entity</th><th style={S.th}>Importance</th>
 <th style={S.th}>Recalls</th><th style={S.th}>Age</th>
 </tr></thead>
 <tbody>
 {memories.map(m => {
 const age = Math.floor((Date.now() - new Date(m.created_at).getTime()) / 86400000);
 return (
 <tr key={m.id} style={{ cursor: 'default' }}>
 <td style={S.td}><span style={S.pill(MEMORY_COLORS[m.memory_type] || C.muted)}>{m.memory_type}</span></td>
 <td style={{ ...S.td, maxWidth: 340 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.summary}</div></td>
 <td style={S.td}>{m.entity_type ? <span style={S.mono}>{m.entity_type}</span> : <span style={{ color: C.dim }}>—</span>}</td>
 <td style={S.td}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <div style={{ width: 40, height: 4, background: C.border, borderRadius: 2 }}>
 <div style={{ height: '100%', width: `${m.importance}%`, background: m.importance >= 70 ? C.crimson : m.importance >= 50 ? C.gold : C.sage, borderRadius: 2 }} />
 </div>
 <span style={{ ...S.mono, color: C.text }}>{m.importance}</span>
 </div>
 </td>
 <td style={{ ...S.td, ...S.mono }}>{m.recall_count}</td>
 <td style={{ ...S.td, ...S.mono }}>{age === 0 ? 'today' : `${age}d`}</td>
 </tr>
 );
 })}
 </tbody>
 </table>
 )}
 {total > memories.length && <div style={{ ...S.mono, textAlign: 'center', padding: '10px 0', color: C.muted }}>Showing {memories.length} of {total}</div>}
 </div>
 </div>
 );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: A2A Messaging (5.2)
// ─────────────────────────────────────────────────────────────────────────────
function MessagingTab() {
 const [data, setData] = useState<{ messages: AgentMessage[]; total: number } | null>(null);
 const [agentFilter, setAgentFilter] = useState('');

 const load = useCallback(() => {
 agentIntelApi.getMessageLog({ agent: agentFilter || undefined, limit: 50 })
 .then(setData).catch(() => {});
 }, [agentFilter]);

 useEffect(() => { load(); }, [load]);

 const MSG_COLOR: Record<string, string> = {
 query: C.blue, response: C.sage, delegation: C.gold,
 notification: C.muted, alert: C.crimson, data_share: C.copper,
 };
 const STATUS_COLOR: Record<string, string> = {
 sent: C.muted, delivered: C.blue, read: C.sage, replied: C.copper, expired: C.dim,
 };

 return (
 <div>
 <div style={{ ...S.card, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
 <div style={S.label}>Agent Filter</div>
 <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)} style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, padding: '6px 12px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', flex: 1 }}>
 <option value="">All Agents</option>
 {Object.entries(AGENT_META).slice(0, 6).map(([k, v]) => <option key={k} value={k}>{v.label} Agent</option>)}
 </select>
 <button onClick={load} style={S.btn('ghost')}>↻ Refresh</button>
 {data && <span style={{ ...S.mono, color: C.muted }}>{data.total} messages</span>}
 </div>

 <div style={S.card}>
 {!data || data.messages.length === 0 ? (
 <div style={S.empty}>No agent messages yet. Messages appear when agents communicate via the Redis pub/sub bus.</div>
 ) : (
 <table style={S.table}>
 <thead><tr>
 <th style={S.th}>From → To</th><th style={S.th}>Type</th>
 <th style={S.th}>Subject</th><th style={S.th}>Status</th>
 <th style={S.th}>Reply</th><th style={S.th}>Time</th>
 </tr></thead>
 <tbody>
 {data.messages.map(m => {
 const fromMeta = AGENT_META[m.from_agent];
 const toMeta = AGENT_META[m.to_agent] || AGENT_META.broadcast;
 return (
 <tr key={m.id}>
 <td style={S.td}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
 <span style={{ color: fromMeta?.color || C.muted }}>{fromMeta?.icon || '?'} {fromMeta?.label || m.from_agent}</span>
 <span style={{ color: C.dim }}>→</span>
 <span style={{ color: toMeta?.color || C.muted }}>{toMeta?.icon || '?'} {toMeta?.label || m.to_agent}</span>
 </div>
 </td>
 <td style={S.td}><span style={S.pill(MSG_COLOR[m.message_type] || C.muted)}>{m.message_type}</span></td>
 <td style={{ ...S.td, maxWidth: 260 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.subject}</div></td>
 <td style={S.td}><span style={S.pill(STATUS_COLOR[m.status] || C.muted)}>{m.status}</span></td>
 <td style={S.td}><span style={{ color: m.has_response ? C.sage : C.dim }}>{m.has_response ? '✓' : '—'}</span></td>
 <td style={{ ...S.td, ...S.mono }}>{new Date(m.created_at).toLocaleTimeString()}</td>
 </tr>
 );
 })}
 </tbody>
 </table>
 )}
 </div>
 </div>
 );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Scoring Queue (5.3)
// ─────────────────────────────────────────────────────────────────────────────
function ScoringQueueTab() {
 const [data, setData] = useState<{ stats: ScoringQueueStats; recent_events: ScoringEvent[] } | null>(null);
 const [triggering, setTriggering] = useState(false);
 const [supplierId, setSupplierId] = useState('');

 useEffect(() => { agentIntelApi.getScoringQueue().then(setData).catch(() => {}); }, []);

 const trigger = async () => {
 if (!supplierId.trim()) return;
 setTriggering(true);
 try {
 const r = await agentIntelApi.triggerRescore(supplierId.trim());
 alert(r.message);
 agentIntelApi.getScoringQueue().then(setData).catch(() => {});
 } catch (e: any) { alert(e.message); }
 finally { setTriggering(false); }
 };

 const STATUS_COLOR: Record<string, string> = { pending: C.gold, processing: C.blue, scored: C.sage, failed: C.crimson, skipped: C.muted };
 const TRIGGER_COLOR: Record<string, string> = { recall_activated: C.crimson, iot_violation: C.amber, supplier_compliance: C.copper, manual: C.muted, import: C.blue };

 return (
 <div>
 {data?.stats && (
 <div style={S.grid4}>
 <StatCard label="Pending" value={data.stats.pending} color={C.gold} />
 <StatCard label="Processing" value={data.stats.processing} color={C.blue} />
 <StatCard label="Scored Today" value={data.stats.scored_today} color={C.sage} />
 <StatCard label="Failed" value={data.stats.failed} color={C.crimson} />
 </div>
 )}

 <div style={{ ...S.card, display: 'flex', gap: 10, alignItems: 'center' }}>
 <div style={S.label}>Manual Trigger</div>
 <input value={supplierId} onChange={e => setSupplierId(e.target.value)}
 placeholder="Supplier UUID…"
 style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
 <button onClick={trigger} disabled={triggering || !supplierId.trim()} style={S.btn('primary')}>
 {triggering ? 'Triggering…' : '⚡ Trigger Rescore'}
 </button>
 </div>

 <div style={S.card}>
 <div style={S.label}>Recent Scoring Events</div>
 {!data || data.recent_events.length === 0 ? (
 <div style={S.empty}>No scoring events yet. Events fire automatically when supplier/lot/recall changes occur.</div>
 ) : (
 <table style={S.table}>
 <thead><tr>
 <th style={S.th}>Product</th><th style={S.th}>Trigger</th>
 <th style={S.th}>Priority</th><th style={S.th}>Status</th>
 <th style={S.th}>Score Δ</th><th style={S.th}>Time</th>
 </tr></thead>
 <tbody>
 {data.recent_events.map(e => {
 const delta = e.score_after != null && e.score_before != null ? e.score_after - e.score_before : null;
 return (
 <tr key={e.id}>
 <td style={S.td}>{e.product_name}</td>
 <td style={S.td}><span style={S.pill(TRIGGER_COLOR[e.trigger_type] || C.muted)}>{e.trigger_type.replace(/_/g, ' ')}</span></td>
 <td style={S.td}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <div style={{ width: 32, height: 3, background: C.border, borderRadius: 2 }}>
 <div style={{ height: '100%', width: `${e.priority}%`, background: e.priority >= 80 ? C.crimson : e.priority >= 60 ? C.gold : C.sage, borderRadius: 2 }} />
 </div>
 <span style={S.mono}>{e.priority}</span>
 </div>
 </td>
 <td style={S.td}><span style={S.pill(STATUS_COLOR[e.status] || C.muted)}>{e.status}</span></td>
 <td style={S.td}>
 {delta != null ? <span style={{ color: delta > 0 ? C.sage : delta < 0 ? C.crimson : C.muted, fontWeight: 700 }}>{delta > 0 ? '+' : ''}{delta}</span> : <span style={{ color: C.dim }}>—</span>}
 </td>
 <td style={{ ...S.td, ...S.mono }}>{new Date(e.created_at).toLocaleTimeString()}</td>
 </tr>
 );
 })}
 </tbody>
 </table>
 )}
 </div>
 </div>
 );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Router Stats (5.4)
// ─────────────────────────────────────────────────────────────────────────────
function RouterStatsTab() {
 const [stats, setStats] = useState<RouterStats | null>(null);
 const [days, setDays] = useState(30);

 useEffect(() => { agentIntelApi.getRouterStats(days).then(setStats).catch(() => {}); }, [days]);

 const formatCost = (n: number) => n < 0.001 ? `$${(n * 1000).toFixed(3)}m` : `$${n.toFixed(4)}`;

 return (
 <div>
 <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
 {[7, 30, 90].map(d => (
 <button key={d} onClick={() => setDays(d)} style={{ ...S.btn(days === d ? 'primary' : 'ghost'), padding: '6px 14px' }}>{d}d</button>
 ))}
 </div>

 {stats && (
 <>
 <div style={S.grid3}>
 <StatCard label="Total API Calls" value={stats.total_calls.toLocaleString()} color={C.blue} />
 <StatCard label="Estimated Cost" value={formatCost(stats.total_cost_usd)} color={C.gold} sub={`Last ${days} days`} />
 <StatCard label="Cost Per Call" value={stats.total_calls ? formatCost(stats.total_cost_usd / stats.total_calls) : '$0'} color={C.sage} />
 </div>

 <div style={S.grid2}>
 <div style={{ ...S.card, marginBottom: 0 }}>
 <div style={S.label}>Routing Distribution</div>
 {stats.by_tier.map(t => (
 <div key={t.tier} style={{ marginBottom: 12 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
 <span style={{ ...S.pill(TIER_COLOR[t.tier] || C.muted), textTransform: 'capitalize' }}>{t.tier}</span>
 <span style={S.mono}>{t.calls} calls · {formatCost(t.cost_usd)}</span>
 </div>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
 <div style={{ height: '100%', width: `${t.pct}%`, background: TIER_COLOR[t.tier] || C.dim, borderRadius: 3 }} />
 </div>
 <span style={{ ...S.mono, minWidth: 32, textAlign: 'right' }}>{t.pct}%</span>
 </div>
 </div>
 ))}
 {stats.by_tier.length === 0 && <div style={S.empty}>No routing data yet</div>}
 </div>

 <div style={{ ...S.card, marginBottom: 0 }}>
 <div style={S.label}>Top Task Types</div>
 <table style={S.table}>
 <thead><tr>
 <th style={S.th}>Task</th><th style={S.th}>Calls</th><th style={S.th}>Avg Cost</th>
 </tr></thead>
 <tbody>
 {stats.by_task.map(t => (
 <tr key={t.task_type}>
 <td style={{ ...S.td, ...S.mono }}>{t.task_type.replace(/_/g, ' ')}</td>
 <td style={{ ...S.td, ...S.mono }}>{t.calls}</td>
 <td style={{ ...S.td, ...S.mono, color: C.gold }}>{formatCost(t.avg_cost_usd)}</td>
 </tr>
 ))}
 {stats.by_task.length === 0 && <tr><td colSpan={3} style={{ ...S.td, textAlign: 'center', color: C.dim }}>No data yet</td></tr>}
 </tbody>
 </table>
 </div>
 </div>

 {stats.daily.length > 0 && (
 <div style={S.card}>
 <div style={S.label}>Daily Call Volume (last {Math.min(days, stats.daily.length)} days)</div>
 <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 60, marginTop: 8 }}>
 {stats.daily.slice(-30).map((d, i) => {
 const total = d.haiku + d.sonnet + d.opus;
 const max = Math.max(...stats.daily.map(x => x.haiku + x.sonnet + x.opus), 1);
 return (
 <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', gap: 1, cursor: 'default' }} title={`${d.date}: ${total} calls`}>
 {d.opus > 0 && <div style={{ height: `${(d.opus / max) * 100}%`, background: C.copper, borderRadius: '1px 1px 0 0', minHeight: 2 }} />}
 {d.sonnet > 0 && <div style={{ height: `${(d.sonnet / max) * 100}%`, background: C.blue, minHeight: 2 }} />}
 {d.haiku > 0 && <div style={{ height: `${(d.haiku / max) * 100}%`, background: C.sage, minHeight: 2 }} />}
 </div>
 );
 })}
 </div>
 <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
 {[['Haiku', C.sage], ['Sonnet', C.blue], ['Opus', C.copper]].map(([label, color]) => (
 <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted }}>
 <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
 {label}
 </div>
 ))}
 </div>
 </div>
 )}
 </>
 )}
 </div>
 );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Schema Registry (5.5)
// ─────────────────────────────────────────────────────────────────────────────
function SchemaRegistryTab() {
 const [schemas, setSchemas] = useState<AgentSchema[]>([]);

 useEffect(() => { agentIntelApi.getSchemas().then(d => setSchemas(d.schemas)).catch(() => {}); }, []);

 const byAgent = schemas.reduce((acc, s) => {
 if (!acc[s.agent_type]) acc[s.agent_type] = [];
 acc[s.agent_type].push(s);
 return acc;
 }, {} as Record<string, AgentSchema[]>);

 return (
 <div>
 <div style={{ ...S.card, background: '#0a1020', borderColor: '#1a2840', marginBottom: 14 }}>
 <div style={{ fontSize: 11, color: C.blue, fontWeight: 700, marginBottom: 6 }}>🔒 Zero Parse Errors via tool_use</div>
 <div style={{ fontSize: 12, color: '#8aA8c8', lineHeight: 1.6 }}>
 All agent responses use Claude's <code style={{ background: '#0a1830', padding: '1px 6px', borderRadius: 3 }}>tool_use</code> mechanism. The model is forced to return structured JSON matching the registered schema — eliminating the class of bugs where malformed JSON silently fails. Success rates are tracked per schema version.
 </div>
 </div>

 {(Object.entries(byAgent) as [string, AgentSchema[]][]).map(([agentType, agentSchemas]) => {
 const meta = AGENT_META[agentType];
 return (
 <div key={agentType} style={S.card}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
 <span style={{ fontSize: 18 }}>{meta?.icon || '?'}</span>
 <span style={{ fontSize: 14, fontWeight: 700, color: meta?.color || C.muted }}>{meta?.label || agentType} Agent</span>
 <span style={{ ...S.pill(C.blue), marginLeft: 'auto' }}>{agentSchemas.length} schema{agentSchemas.length !== 1 ? 's' : ''}</span>
 </div>
 <table style={S.table}>
 <thead><tr>
 <th style={S.th}>Task Type</th><th style={S.th}>Tool Name</th>
 <th style={S.th}>Version</th><th style={S.th}>Success Rate</th><th style={S.th}>Active</th>
 </tr></thead>
 <tbody>
 {agentSchemas.map(s => (
 <tr key={s.task_type}>
 <td style={{ ...S.td, ...S.mono, color: C.text }}>{s.task_type.replace(/_/g, ' ')}</td>
 <td style={{ ...S.td, ...S.mono }}>{s.tool_name}</td>
 <td style={{ ...S.td, ...S.mono }}>{s.schema_version}</td>
 <td style={S.td}>
 {s.success_rate != null ? (
 <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <Bar value={s.success_rate} max={100} color={s.success_rate >= 90 ? C.sage : s.success_rate >= 70 ? C.gold : C.crimson} />
 <span style={{ ...S.mono, color: C.text, minWidth: 36 }}>{s.success_rate.toFixed(1)}%</span>
 </div>
 ) : <span style={{ color: C.dim }}>—</span>}
 </td>
 <td style={S.td}><span style={{ color: s.is_active ? C.sage : C.crimson, fontWeight: 700 }}>{s.is_active ? '✓' : '✗'}</span></td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 );
 })}
 {schemas.length === 0 && <div style={S.empty}>Loading schemas…</div>}
 </div>
 );
}

// ─────────────────────────────────────────────────────────────────────────────
// Root Page
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
 { id: 'network', label: '◈ Network' },
 { id: 'memory', label: '◎ Memory' },
 { id: 'messages', label: '⇄ Messaging' },
 { id: 'scoring', label: '⚡ Scoring Queue' },
 { id: 'router', label: '⊕ Model Router' },
 { id: 'schemas', label: '⋈ Schema Registry' },
] as const;

type TabId = typeof TABS[number]['id'];

export default function AgentsPage() {
 const [tab, setTab] = useState<TabId>('network');
 return (
 <div style={S.page}>
 <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap');`}</style>
 <div style={S.h1}>Agent Intelligence Network</div>
 <div style={S.sub}>v7 Phase 5 — Memory · Messaging · Proactive Scoring · Model Router · Structured Output</div>

 <div style={S.tabs}>
 {TABS.map(t => <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>{t.label}</button>)}
 </div>

 {tab === 'network' && <NetworkTab />}
 {tab === 'memory' && <MemoryTab />}
 {tab === 'messages' && <MessagingTab />}
 {tab === 'scoring' && <ScoringQueueTab />}
 {tab === 'router' && <RouterStatsTab />}
 {tab === 'schemas' && <SchemaRegistryTab />}
 </div>
 );
}
